import {
  ChangeEvent, useCallback, useEffect, useState,
} from 'react';
import { generateRandomPassword } from '../../services/generator';

type TCheckboxChangeFunction = (
  e: ChangeEvent<HTMLInputElement>, value: boolean
) => void | Promise<void>;
type TSliderChangeFunction = (e: Event, value: number | number[]) => void

export interface IPasswordGeneratorPropsFromHook {
  handleAreLettersAllowedChange: TCheckboxChangeFunction;
  handleAreNumbersAllowedChange: TCheckboxChangeFunction;
  handleAreSpecialCharactersAllowedChange: TCheckboxChangeFunction;
  areLettersAllowed: boolean;
  areSpecialCharactersAllowed: boolean;
  areNumbersAllowed: boolean;
  handleNumberOfCharactersChange: TSliderChangeFunction;
  numberOfCharacters: number;
}

export interface IUsePasswordGeneratorReturn {
  passwordGeneratorProps: IPasswordGeneratorPropsFromHook;
  regenerate: () => void;
  password: string;
}

export interface IUsePasswordGeneratorProps {
  areNumbersAllowedDefaultValue?: boolean
  areLettersAllowedDefaultValue?: boolean
  areSpecialCharactersAllowedDefaultValue?: boolean
  numberOfCharactersDefaultValue?: number
  generateOnInit?: boolean
}

const DEFAULT_PROPS = {
  areLettersAllowedDefaultValue: true,
  areNumbersAllowedDefaultValue: true,
  areSpecialCharactersAllowedDefaultValue: true,
  generateOnInit: true,
  numberOfCharactersDefaultValue: 20,
};

export const usePasswordGenerator = (
  props?: IUsePasswordGeneratorProps,
): IUsePasswordGeneratorReturn => {
  const {
    areLettersAllowedDefaultValue = DEFAULT_PROPS.areLettersAllowedDefaultValue,
    areNumbersAllowedDefaultValue = DEFAULT_PROPS.areNumbersAllowedDefaultValue,
    areSpecialCharactersAllowedDefaultValue = DEFAULT_PROPS.areSpecialCharactersAllowedDefaultValue,
    generateOnInit = DEFAULT_PROPS.generateOnInit,
    numberOfCharactersDefaultValue = DEFAULT_PROPS.numberOfCharactersDefaultValue,
  } = props || {};

  const [isTouched, setIsTouched] = useState(false);
  const [areNumbersAllowed, setAreNumbersAllowed] = useState(areNumbersAllowedDefaultValue);
  const [areLettersAllowed, setAreLettersAllowed] = useState(areLettersAllowedDefaultValue);
  const [areSpecialCharactersAllowed, setAreSpecialCharactersAllowed] = useState(
    areSpecialCharactersAllowedDefaultValue,
  );
  const [numberOfCharacters, setNumberOfCharacters] = useState(numberOfCharactersDefaultValue);

  const [password, setPassword] = useState(() => (generateOnInit ? generateRandomPassword({
    includeSpecialCharacters: areSpecialCharactersAllowed,
    includeNumbers: areNumbersAllowed,
    includeLetters: areLettersAllowed,
    length: numberOfCharacters,
  }) : ''));

  const generatePassword = useCallback(() => {
    const generatedPassword = generateRandomPassword({
      includeSpecialCharacters: areSpecialCharactersAllowed,
      includeNumbers: areNumbersAllowed,
      includeLetters: areLettersAllowed,
      length: numberOfCharacters,
    });
    setPassword(generatedPassword);
  }, [
    areNumbersAllowed,
    areLettersAllowed,
    areSpecialCharactersAllowed,
    numberOfCharacters,
  ]);

  const handleAreNumbersAllowedChange = useCallback<TCheckboxChangeFunction>((e) => {
    setAreNumbersAllowed(e.target.checked);
    if (!isTouched) {
      setIsTouched(true);
    }
  }, [isTouched]);

  const handleAreLettersAllowedChange = useCallback<TCheckboxChangeFunction>((e) => {
    setAreLettersAllowed(e.target.checked);
    if (!isTouched) {
      setIsTouched(true);
    }
  }, [isTouched]);

  const handleAreSpecialCharactersAllowedChange = useCallback<TCheckboxChangeFunction>((e) => {
    setAreSpecialCharactersAllowed(e.target.checked);
    if (!isTouched) {
      setIsTouched(true);
    }
  }, [isTouched]);

  const handleNumberOfCharactersChange = useCallback<TSliderChangeFunction>((_, value) => {
    if (numberOfCharacters === value) {
      return;
    }
    setNumberOfCharacters(value as number);
    if (!isTouched) {
      setIsTouched(true);
    }
  }, [numberOfCharacters, isTouched]);

  useEffect(() => {
    if (!isTouched) {
      return;
    }
    generatePassword();
  }, [
    generatePassword,
    isTouched,
  ]);

  return {
    password,
    regenerate: generatePassword,
    passwordGeneratorProps: {
      areNumbersAllowed,
      areLettersAllowed,
      areSpecialCharactersAllowed,
      numberOfCharacters,
      handleAreSpecialCharactersAllowedChange,
      handleAreLettersAllowedChange,
      handleAreNumbersAllowedChange,
      handleNumberOfCharactersChange,
    },
  };
};

export default usePasswordGenerator;
